import React, { useEffect, useState } from "react";
import { Stack, useLocalSearchParams, useRouter, usePathname } from "expo-router";
import { trpc } from "@/trpc/react";
import { TouchableOpacity, View, Text, Modal, FlatList, Pressable, Alert } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCrew } from "@/context/CrewProvider";
import colors from '@/constants/Colors';

// Define the participant type
interface Participant {
    id: number;
    type: string;
    userId: number | null;
    name: string;
    role?: string | null;
    joinedAt?: Date;
}

// Component for the custom header with participants icon
function ParticipantsButton({ participants = [] }: { participants: Participant[] }) {
    const [modalVisible, setModalVisible] = useState(false);

    const handlePress = () => {
        if (participants.length === 0) {
            Alert.alert("No participants", "There are no other participants in this conversation.");
            return;
        }
        setModalVisible(true);
    };

    return (
        <>
            <TouchableOpacity
                onPress={handlePress}
                className="mr-2 px-2"
            >
                <View className="flex-row items-center">
                    <Ionicons name="people" size={22} color={colors.accent} />
                    <Text className="ml-1 text-sm font-medium" style={{ color: colors.accent }}>
                        {participants.length}
                    </Text>
                </View>
            </TouchableOpacity>

            <Modal
                animationType="fade"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <Pressable
                    className="flex-1 bg-black/50 justify-center items-center"
                    onPress={() => setModalVisible(false)}
                >
                    <View className="bg-white w-[80%] rounded-lg p-4 max-h-[60%]">
                        <View className="flex-row justify-between items-center mb-4">
                            <Text className="text-lg font-bold">Participants</Text>
                            <TouchableOpacity onPress={() => setModalVisible(false)}>
                                <Ionicons name="close" size={24} color="#333" />
                            </TouchableOpacity>
                        </View>

                        <FlatList
                            data={participants}
                            keyExtractor={(item) => item.id.toString()}
                            renderItem={({ item }) => (
                                <View className="py-2 border-b border-gray-200">
                                    <Text className="text-base">{item.name}</Text>
                                    {item.role && (
                                        <Text className="text-xs text-gray-500">{item.role}</Text>
                                    )}
                                </View>
                            )}
                        />
                    </View>
                </Pressable>
            </Modal>
        </>
    );
}

export default function ConversationLayout() {
    const params = useLocalSearchParams();
    const conversationId = typeof params.id === 'string' ? parseInt(params.id, 10) : 0;
    const { currentCrewMember } = useCrew();
    const router = useRouter();

    // Get conversation details to use as the header title
    const { data: conversation } = trpc.messages.getMessagesForConversation.useQuery(
        {
            conversation_id: conversationId,
            crew_id: currentCrewMember?.id // Add crew_id to mark messages as read
        },
        {
            enabled: !!conversationId && conversationId > 0 && !!currentCrewMember?.id,
        }
    );

    // Filter out current user from participants list
    const otherParticipants = conversation?.participants?.filter(
        p => p.type === 'crew' && p.userId !== currentCrewMember?.id
    ) || [];

    // Handler for back button
    const handleBackPress = () => {
        router.push('/messages');
    };

    return (
        <Stack
            screenOptions={{
                headerShown: true,
                headerBackVisible: false, // Hide default back button
                headerBackButtonDisplayMode: 'minimal', // Hide back button labels
                headerStyle: {
                    backgroundColor: colors.primary,
                },
                headerTintColor: colors.accent,
            }}
        >
            <Stack.Screen
                name="index"
                options={{
                    headerTitle: conversation?.conversation?.name || "Message",
                    headerTitleAlign: "center",
                    headerLeft: () => (
                        <TouchableOpacity onPress={handleBackPress} className="">
                            <View className="flex-row items-center">
                                <Ionicons name="chevron-back" size={28} color={colors.accent} />
                            </View>
                        </TouchableOpacity>
                    ),
                    headerRight: () => <ParticipantsButton participants={otherParticipants} />
                }}
            />
        </Stack>
    );
} 